import { useMemo } from "react";
import { searchTasks } from "../lib/utils";
import { formatLocalDate } from "../lib/workingHours";
import { taskMatchesScheduledFilters } from "../lib/scheduledFilters";
import {
  EMPTY_LIST_MEMBERS,
  isListShared,
  orderedListsForBoard,
} from "../lib/listSharing";
import type {
  Task,
  List,
  ListMember,
  TaskFilter,
  BoardColumn,
  BuiltInFilterState,
  SortDirection,
} from "../types";

// Shared lists (#286): "mine" keeps only Tasks this user authored,
// regardless of List; "shared" keeps only Tasks whose List is shared,
// regardless of author — matching ios/SCHEMA.md's description of
// task_filters.task_scope. currentUserId/sharedListIds are optional so
// every existing applyFilter call (and every fixture) keeps typing without
// a mechanical update; task_scope itself defaults to "all" at the DB layer.
function matchesTaskScope(
  task: Task,
  filter: TaskFilter,
  currentUserId?: string,
  sharedListIds?: Set<string>,
): boolean {
  if (!filter.task_scope || filter.task_scope === "all") return true;
  if (filter.task_scope === "mine") return task.user_id === currentUserId;
  return !!task.list_id && !!sharedListIds?.has(task.list_id);
}

function applyFilter(
  tasks: Task[],
  filter: TaskFilter,
  showCompleted: boolean,
  currentUserId?: string,
  sharedListIds?: Set<string>,
): Task[] {
  return tasks.filter((task) => {
    if (filter.date_filters.length > 0) {
      // Local, not UTC: scheduled_date is a local calendar date, so deriving
      // "today" from toISOString() reports yesterday for UTC+X users between
      // local midnight and X:00 (and tomorrow for UTC-X users late evening).
      const today = formatLocalDate(new Date());
      // Sub-tasks are rendered nested inside their parent's card, never as
      // standalone board items, so a sub-task due today can only surface if
      // its parent counts as a match too — otherwise the whole card
      // collapses (see BoardColumn's visible-height animation) and the
      // sub-task becomes unreachable. Gate on showCompleted the same way
      // TaskCard itself decides which subtasks to render.
      const eligibleSubtasks = (task.subtasks ?? []).filter(
        (st) => showCompleted || !st.completed,
      );
      const matchesDateFilter = filter.date_filters.some((df) => {
        if (df === "no-date") return task.scheduled_date === null;
        if (df === "today")
          return (
            task.scheduled_date === today ||
            eligibleSubtasks.some((st) => st.scheduled_date === today)
          );
        if (df === "overdue")
          // Persistent (Carry over) tasks self-manage — they roll
          // forward via the reschedule cron — so they don't surface as overdue,
          // matching the built-in "overdue" filter's behaviour.
          return (
            !!task.scheduled_date &&
            task.scheduled_date < today &&
            !task.completed &&
            !task.persistent
          );
        if (df === "planned") return task.scheduled_date !== null;
        if (df === "deadline") return task.deadline_date !== null;
        return false;
      });
      if (!matchesDateFilter) return false;
    }

    if (filter.list_ids.length > 0) {
      if (!filter.list_ids.some((listId) => task.list_id === listId))
        return false;
    }

    if (filter.priorities.length > 0) {
      if (!filter.priorities.includes(task.priority)) return false;
    }

    if (filter.tag_ids.length > 0) {
      const taskTagIds = task.tags?.map((t) => t.id) || [];
      if (!filter.tag_ids.some((tagId) => taskTagIds.includes(tagId)))
        return false;
    }

    if (filter.work_hours_filter === "only" && !task.working_hours_only)
      return false;
    if (filter.work_hours_filter === "exclude" && task.working_hours_only)
      return false;

    if (!matchesTaskScope(task, filter, currentUserId, sharedListIds)) return false;

    return true;
  });
}

interface UseBoardFilterArgs {
  tasks: Task[];
  lists: List[];
  filters: TaskFilter[];
  showArchived: boolean;
  showCompleted: boolean;
  searchTerm: string;
  activeFilterId: string | undefined;
  builtInFilterState: BuiltInFilterState;
  sortByPriority: SortDirection;
  sortByCreatedDate: SortDirection;
  viewMode?: "list" | "priority";
  // Shared lists (#286) — optional so every existing caller/fixture keeps
  // typing without a mechanical update. currentUserId powers the saved
  // filter's task_scope "mine" branch; listMembers powers task_scope
  // "shared", the board column mark/tint, and the Member-board pinning
  // order (owned Lists first, then accepted-Membership Lists).
  currentUserId?: string;
  listMembers?: ListMember[];
}

interface UseBoardFilterResult {
  columns: BoardColumn[];
  listlessTasks: Task[];
  matchingTaskIds: Set<string>;
  isFilterActive: boolean;
}

export function useBoardFilter({
  tasks,
  lists,
  filters,
  showArchived,
  showCompleted,
  searchTerm,
  activeFilterId,
  builtInFilterState,
  sortByPriority,
  sortByCreatedDate,
  viewMode = "list",
  currentUserId,
  listMembers = EMPTY_LIST_MEMBERS,
}: UseBoardFilterArgs): UseBoardFilterResult {
  const sharedListIds = useMemo(
    () => new Set(listMembers.map((m) => m.list_id)),
    [listMembers],
  );

  const hasBuiltInFilters =
    builtInFilterState.priorities.length > 0 ||
    builtInFilterState.listIds.length > 0 ||
    builtInFilterState.tagIds.length > 0 ||
    builtInFilterState.scheduledFilter.length > 0 ||
    !!builtInFilterState.workHoursFilter;

  const isFilterActive = !!(
    activeFilterId ||
    hasBuiltInFilters ||
    searchTerm.trim()
  );

  const matchingTaskIds = useMemo(() => {
    let pool = showArchived
      ? tasks.filter((t) => t.archived)
      : tasks.filter((t) => !t.archived);
    if (!showCompleted) pool = pool.filter((t) => !t.completed);

    if (searchTerm.trim()) {
      return new Set(searchTasks(pool, searchTerm).map((r) => r.task.id));
    }

    // A saved filter and the built-in filters stack (#744): the saved filter
    // narrows the pool first, then the built-in filters narrow it further.
    if (activeFilterId) {
      const activeFilter = filters.find((f) => f.id === activeFilterId);
      if (activeFilter) {
        pool = applyFilter(pool, activeFilter, showCompleted, currentUserId, sharedListIds);
      }
    }

    if (hasBuiltInFilters) {
      let filtered = pool;

      if (builtInFilterState.scheduledFilter.length > 0) {
        // Local, not UTC — see the note in applyFilter above.
        const today = formatLocalDate(new Date());
        filtered = filtered.filter((task) =>
          taskMatchesScheduledFilters(
            task,
            builtInFilterState.scheduledFilter,
            today,
          ),
        );
      }

      // Apply work-hours filter ("only" → show only working-hours tasks,
      // "exclude" → hide them, null → no constraint). Useful for weekend
      // mode (exclude) and Monday-morning focus (only).
      if (builtInFilterState.workHoursFilter === "only") {
        filtered = filtered.filter((task) => !!task.working_hours_only);
      } else if (builtInFilterState.workHoursFilter === "exclude") {
        filtered = filtered.filter((task) => !task.working_hours_only);
      }

      // Apply the list filter inline so the "No list" option (null in
      // listIds) can match tasks without a list_id. applyFilter's TaskFilter
      // type uses string[] for list_ids, so the listless filter lives here.
      if (builtInFilterState.listIds.length > 0) {
        filtered = filtered.filter((task) =>
          builtInFilterState.listIds.includes(task.list_id),
        );
      }

      const syntheticFilter: TaskFilter = {
        id: "__built_in__",
        user_id: "",
        name: "",
        date_filters: [],
        list_ids: [],
        priorities: builtInFilterState.priorities,
        tag_ids: builtInFilterState.tagIds,
        order_position: 0,
        created_at: "",
        updated_at: "",
      };
      return new Set(
        applyFilter(filtered, syntheticFilter, showCompleted).map((t) => t.id),
      );
    }

    return new Set(pool.map((t) => t.id));
  }, [
    tasks,
    showCompleted,
    showArchived,
    searchTerm,
    activeFilterId,
    filters,
    builtInFilterState,
    hasBuiltInFilters,
    currentUserId,
    sharedListIds,
  ]);

  const { columns, listlessTasks } = useMemo(() => {
    let pool = showArchived
      ? tasks.filter((t) => t.archived)
      : tasks.filter((t) => !t.archived);
    if (!showCompleted) pool = pool.filter((t) => !t.completed);

    if (sortByPriority !== "off") {
      const dir = sortByPriority === "asc" ? 1 : -1;
      pool = [...pool].sort((a, b) => dir * (a.priority - b.priority));
    }

    // Sort by created date if enabled (asc = newest first)
    if (sortByCreatedDate !== "off") {
      const dir = sortByCreatedDate === "asc" ? -1 : 1;
      pool = [...pool].sort(
        (a, b) =>
          dir *
          (new Date(a.created_at).getTime() -
            new Date(b.created_at).getTime()),
      );
    }

    if (viewMode === "list") {
      // Shared lists (#286): owned Lists first (in their existing order),
      // then accepted-Membership Lists pinned after — a Member can't
      // rearrange a Shared list's position (deferred-edges doc). Falls back
      // to `lists` as given when currentUserId is unknown (e.g. mid sign-in).
      const orderedLists = currentUserId
        ? orderedListsForBoard(lists, listMembers, currentUserId)
        : lists;
      const cols: BoardColumn[] = orderedLists.map((l) => ({
        name: l.name,
        emoji: l.emoji,
        listId: l.id,
        tasks: pool.filter((t) => t.list_id === l.id),
        isShared: isListShared(l.id, listMembers),
      }));
      return { columns: cols, listlessTasks: pool.filter((t) => !t.list_id) };
    }

    return {
      columns: [
        {
          name: "High Priority",
          emoji: "🔴",
          tasks: pool.filter((t) => t.priority === 1),
        },
        {
          name: "Medium Priority",
          emoji: "🟠",
          tasks: pool.filter((t) => t.priority === 2),
        },
        {
          name: "Low Priority",
          emoji: "🟡",
          tasks: pool.filter((t) => t.priority === 3),
        },
      ],
      listlessTasks: [],
    };
  }, [
    tasks,
    lists,
    showArchived,
    showCompleted,
    sortByPriority,
    sortByCreatedDate,
    viewMode,
    currentUserId,
    listMembers,
  ]);

  return { columns, listlessTasks, matchingTaskIds, isFilterActive };
}
